import React, { useEffect, useRef, useState } from "react";
import "./hero-video.component.styles.scss";

interface IHeroVideoProps {
  /** Ambient loop, e.g. "/heroes/home-loop.mp4" (H.264, muted, loopable). */
  mp4Src: string;
  /** Poster image — it stays the LCP element; the video fades in over it. */
  imageUrl: string;
  imageWebp?: {
    xl?: string;
    md?: string;
    sm?: string;
  };
  imageAlt: string;
  /** Layout classes from the caller, e.g. "lvs-cover__photo fx-rise-2". */
  className?: string;
}

// A hero photo that starts breathing when circumstances allow.
//
// The <picture> below is always rendered — crawlers, no-JS, reduced-motion,
// phones, Save-Data, slow connections and any video failure all end at the
// still photo; the hero can never look broken. The <video> is a strictly
// desktop-class enhancement: it waits for the window load event (never
// contends with the LCP), mounts on intersection, starts via an explicit
// play() (no autoPlay attribute, so preload="metadata" stays honest), fades
// in once frames actually play, pauses offscreen, and a failed load latches
// terminally — no refetch loops on flaky networks.
const HeroVideo: React.FC<IHeroVideoProps> = ({
  mp4Src,
  imageUrl,
  imageWebp,
  imageAlt,
  className,
}) => {
  const figureRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const failedRef = useRef(false);
  const [armed, setArmed] = useState(false);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure || !mp4Src) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    // Phones keep the poster: a 1.4MB ambient loop is not worth mobile data,
    // and iOS exposes no Save-Data/connection signal to check instead.
    if (window.matchMedia?.("(max-width: 767px)").matches) return undefined;
    const nav = navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    };
    if (nav.connection?.saveData) return undefined;
    if (nav.connection?.effectiveType && nav.connection.effectiveType !== "4g") {
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      setArmed(true);
      return undefined;
    }

    let disposed = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (failedRef.current) return;
        const video = videoRef.current;
        if (entry.isIntersecting) {
          setArmed(true);
          if (video) {
            video.muted = true; // defensive: autoplay policies want the property
            video.play()?.catch(() => {});
          }
        } else {
          video?.pause();
        }
      },
      { threshold: 0.1 }
    );

    // Never race the poster/LCP: start observing only after the page loaded.
    const begin = () => {
      if (!disposed) io.observe(figure);
    };
    if (document.readyState === "complete") begin();
    else window.addEventListener("load", begin, { once: true });

    return () => {
      disposed = true;
      window.removeEventListener("load", begin);
      io.disconnect();
    };
  }, [mp4Src]);

  useEffect(() => {
    if (!armed) return;
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.play()?.catch(() => {});
  }, [armed]);

  return (
    <figure ref={figureRef} className={`lvs-heroVideo${className ? ` ${className}` : ""}`}>
      <picture>
        {imageWebp?.sm && (
          <source media="(max-width: 767px)" srcSet={imageWebp.sm} type="image/webp" />
        )}
        {imageWebp?.md && (
          <source media="(max-width: 1279px)" srcSet={imageWebp.md} type="image/webp" />
        )}
        {imageWebp?.xl && <source srcSet={imageWebp.xl} type="image/webp" />}
        <img
          src={imageUrl}
          alt={imageAlt}
          loading="eager"
          decoding="async"
          {...({ fetchpriority: "high" } as { fetchpriority?: string })}
        />
      </picture>
      {armed && (
        <video
          ref={videoRef}
          className={`lvs-heroVideo__video${live ? " is-live" : ""}`}
          src={mp4Src}
          muted
          playsInline
          loop
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          disablePictureInPicture
          onPlaying={() => setLive(true)}
          onError={() => {
            failedRef.current = true; // terminal: never refetch a failing asset
            setLive(false);
            setArmed(false);
          }}
        />
      )}
    </figure>
  );
};

export default HeroVideo;
